import { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { Box, Button, Menu, MenuItem, Typography } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import PropTypes from 'prop-types';

const MenuOption = ({ name, haveDropDown, dropdownDetail, url, isExternal, btnMinWidth, menuItemMinWidth }) => {
    const location = useLocation();

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (e) => {
        if (anchorEl !== e.currentTarget) {
            setAnchorEl(e.currentTarget);
        }
    };

    const handleClose = () => {
        if (anchorEl && typeof anchorEl.focus === 'function') {
            anchorEl.focus();
        }
        setAnchorEl(null);
    };

    const isMenuOptionActive = () => {
        let color = '#4D4D4D99';
        let fontWeight = 400;
        if (haveDropDown && dropdownDetail.map((item) => item.url).includes(location.pathname)) {
            color = '#4D4D4D';
            fontWeight = 600;
        } else if (haveDropDown && open) {
            fontWeight = 600;
        } else if (!haveDropDown && (url === location.pathname || (url === '/blog' && location.pathname.match(/^\/blog(\/.*)?$/)))) {
            color = '#4D4D4D';
            fontWeight = 600;
        }

        return { color, fontWeight };
    };

    const getArrowColor = () => {
        const homePage = ['/'];
        const mediaServiceRelatedPage = [
            '/media-service',
            '/data-driven-programmatic',
            '/premium-placement',
            '/campaign-management-service',
            '/china-media-solutions',
            '/ai-creation'
        ];
        const dataServiceRelatedPage = ['/data-service', '/external-data', '/insight-report', '/etl-project'];

        let color = 'transparent';
        if (homePage.includes(location.pathname) || location.pathname.match(/^\/blog(\/.*)?$/)) {
            color = '#E15C12';
        } else if (mediaServiceRelatedPage.includes(location.pathname)) {
            color = '#4D92BB';
        } else if (dataServiceRelatedPage.includes(location.pathname)) {
            color = '#7B6CF9';
        } else {
            color = '#E15C12';
        }

        return color;
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
            <Button
                id={`${name.toLowerCase().replaceAll(' ', '-')}-button`}
                variant="text"
                component={!haveDropDown ? (isExternal ? 'a' : Link) : undefined}
                href={!haveDropDown && isExternal ? url : undefined}
                to={!haveDropDown && !isExternal ? url : undefined}
                target={!haveDropDown && isExternal ? '_blank' : undefined}
                rel={!haveDropDown && isExternal ? 'noopener noreferrer' : undefined}
                aria-controls={open ? `${name.toLowerCase().replaceAll(' ', '-')}-menu` : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup={haveDropDown ? 'true' : undefined}
                sx={{
                    minWidth: btnMinWidth ? btnMinWidth : 'unset',
                    fontSize: '16px',
                    marginTop: 0,
                    lineHeight: '30px',
                    alignSelf: 'center',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    '&:hover': {
                        color: '#4D4D4D',
                        fontWeight: 600,
                        backgroundColor: 'transparent'
                    },
                    ...isMenuOptionActive()
                }}
                onTouchStart={handleClick}
                onClick={(e) => {
                    if (haveDropDown) {
                        handleClick(e);
                    }
                }}
                onMouseOver={handleClick}
            >
                {name}
            </Button>
            {haveDropDown && <KeyboardArrowDown sx={{ color: `${getArrowColor()}` }} />}
            {haveDropDown && (
                <Menu
                    id={`${name.toLowerCase().replaceAll(' ', '-')}-menu`}
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    keepMounted
                    disableAutoFocus
                    disableEnforceFocus
                    disableRestoreFocus
                    MenuListProps={{
                        'aria-labelledby': `${name.toLowerCase().replaceAll(' ', '-')}-button`,
                        autoFocusItem: false,
                        onMouseLeave: handleClose
                    }}
                    sx={{ '& .MuiPaper-root': { bgcolor: '#FFFFFFC9', backdropFilter: 'blur(5px)' } }}
                >
                    {dropdownDetail &&
                        dropdownDetail.map((option, index) => (
                            <MenuItem
                                key={index}
                                component={Link}
                                to={option.url}
                                sx={{
                                    fontSize: '16px',
                                    bgcolor: 'transparent !important',
                                    color: '#4D4D4D',
                                    fontWeight: option.url === location.pathname ? 600 : 400,
                                    margin: index > 0 ? '20px 16px 20px 32px' : '20px 16px 20px 8px',
                                    minWidth: menuItemMinWidth ? menuItemMinWidth : 'unset',
                                    textDecoration: 'none',
                                    '&:hover': {
                                        fontWeight: 600,
                                        bgcolor: 'transparent'
                                    }
                                }}
                                onClick={handleClose}
                            >
                                {option.name}
                            </MenuItem>
                        ))}
                </Menu>
            )}
            {/* Same dropdown links in document tree so they appear in page source (SSR). After Menu so it never blocks hover. */}
            {haveDropDown && dropdownDetail && (
                <Box
                    component="nav"
                    aria-label={`${name} submenu`}
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        width: 1,
                        height: 1,
                        margin: -1,
                        padding: 0,
                        overflow: 'hidden',
                        clip: 'rect(0, 0, 0, 0)',
                        whiteSpace: 'nowrap',
                        border: 0,
                        pointerEvents: 'none',
                        '& a': { display: 'inline-block', padding: '4px 8px' }
                    }}
                >
                    {dropdownDetail.map((option, index) => (
                        <Link key={`ssr-${index}`} to={option.url}>
                            {option.name}
                        </Link>
                    ))}
                </Box>
            )}
        </Box>
    );
};

MenuOption.propTypes = {
    name: PropTypes.string,
    haveDropDown: PropTypes.bool,
    dropdownDetail: PropTypes.array,
    url: PropTypes.string
};

export default MenuOption;
